'use client';

import { useEffect, useState, useMemo } from 'react';
import { CheckCircle, AlertCircle, Loader2, HelpCircle } from 'lucide-react';
import { useConnectionStatus, useConnectionStore } from '@/lib/stores/connection-store';
import { useViewportSelection } from '@/lib/stores/viewport-selection';
import { useTotalMatrixStore } from '@/lib/stores/total-matrix-store';
import { DevUserSwitcher } from '@/components/dev/dev-user-switcher';

export function StatusBar() {
  const { neo4j, odoo, edge, gemini, lastCheck } = useConnectionStatus();
  const [, setTick] = useState(0); // Force re-render for time display

  // Start polling on mount, stop on unmount
  useEffect(() => {
    const { startPolling, stopPolling } = useConnectionStore.getState();
    startPolling(30000); // Poll every 30 seconds
    return () => stopPolling();
  }, []);

  // Tick every second to update "Xs ago" display
  useEffect(() => {
    const interval = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(interval);
  }, []);

  const formatTime = (date: Date | null) => {
    if (!date) return 'Never';
    const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    return `${Math.floor(seconds / 3600)}h ago`;
  };

  return (
    <footer className="h-6 bg-status-bar text-status-bar-foreground flex items-center px-4 text-xs">
      {/* Left side - Connection status */}
      <div className="flex items-center gap-4">
        <StatusIndicator label="Edge" status={edge} />
        <StatusIndicator label="Neo4j" status={neo4j} />
        <StatusIndicator label="Odoo" status={odoo} />
        <StatusIndicator label="AI" status={gemini} />
      </div>

      {/* Center - BFF Breadcrumb */}
      <div className="flex-1 flex justify-center">
        <ViewportBreadcrumb />
      </div>

      {/* Right side - Dev user switcher + Help */}
      <div className="flex items-center gap-4">
        <DevUserSwitcher />
        <button className="flex items-center gap-1 hover:text-status-bar-foreground/80 transition-colors">
          <HelpCircle className="w-3 h-3" />
          <span>Help</span>
        </button>
      </div>
    </footer>
  );
}


function ViewportBreadcrumb() {
  const { mode, strategySelection, contentSelection, entityId, selectedNode } = useViewportSelection();
  const getViewportConfig = useTotalMatrixStore((s) => s.getViewportConfig);
  // `getViewportConfig` is a stable store-method ref — it never changes,
  // so it can't signal that the underlying `matrix` finished loading.
  // Subscribe to `matrix` directly and key the memo on it; otherwise the
  // memo caches the first-render `null` (matrix still in flight) and the
  // canvas/copilot segments stay `—` for whatever mode the app booted into.
  const matrix = useTotalMatrixStore((s) => s.matrix);
  const navItems = useTotalMatrixStore((s) => s.nav);

  const config = useMemo(
    () => getViewportConfig(mode, contentSelection),
    [mode, contentSelection, getViewportConfig, matrix],
  );

  // Mode label is BFF-sourced via nav_mode_labels(lang) — the NavItem
  // for the active mode carries `.label`. Fall back to the structural
  // key when no NavItem exists (non-registry modes or first paint
  // before the matrix loads).
  const modeLabel = useMemo(
    () => navItems.find((n) => n.mode === mode)?.label,
    [navItems, mode],
  );

  const canvasStrategy = config?.canvas?.strategy ?? '—';
  const copilotTabs = config?.copilot ?? [];
  const tabStrategies = copilotTabs.map((t) => t.strategy).filter(Boolean);

  // Shorten entity ID for display: RFQ2026/00003 stays, long SIDs truncate
  const shortEntity = entityId
    ? entityId.length > 24 ? entityId.slice(0, 20) + '…' : entityId
    : null;

  // Label first (primary readability), structural key in parens (so
  // engineering can still see which mode key is active without
  // looking it up).
  const modeValue = modeLabel ? `${modeLabel} (${mode})` : mode;

  return (
    <div className="flex items-center gap-1 text-status-bar-foreground/70 font-mono">
      <BreadcrumbSegment label="mode" value={modeValue} />
      <Sep />
      <BreadcrumbSegment label="sel" value={contentSelection ?? '—'} />
      <Sep />
      <BreadcrumbSegment label="canvas" value={canvasStrategy} />
      {tabStrategies.length > 0 && (
        <>
          <Sep />
          <BreadcrumbSegment label="copilot" value={tabStrategies.join(', ')} />
        </>
      )}
      {shortEntity && (
        <>
          <Sep />
          <span className="text-status-bar-foreground/50" title={entityId ?? undefined}>{shortEntity}</span>
        </>
      )}
    </div>
  );
}


function BreadcrumbSegment({ label, value }: { label: string; value: string }) {
  return (
    <span>
      <span className="text-status-bar-foreground/40">{label}:</span>
      <span className="ml-0.5">{value}</span>
    </span>
  );
}


function Sep() {
  return <span className="text-status-bar-foreground/30 mx-0.5">→</span>;
}


function StatusIndicator({
  label,
  status,
}: {
  label: string;
  status: 'connected' | 'disconnected' | 'connecting';
}) {
  return (
    <div className="flex items-center gap-1" data-testid={`status-${label.toLowerCase()}`}>
      {status === 'connected' && <CheckCircle className="w-3 h-3 text-green-400" />}
      {status === 'disconnected' && <AlertCircle className="w-3 h-3 text-red-400" />}
      {status === 'connecting' && <Loader2 className="w-3 h-3 animate-spin" />}
      <span>{label}</span>
    </div>
  );
}
